/**
 * Edit-source resolution for AI document edits.
 *
 * The edit flow must (a) pick exactly one target document and (b) edit its
 * complete, authoritative page set. Truncated relevance context is only a
 * planning hint; it is never a source of truth for what gets persisted.
 */
import { documentFileNamesMatch } from "./revisions.ts";

export type EditableDocumentPage = {
  pageId?: string;
  pageNumber: number;
  title: string;
  html: string;
  qa?: Record<string, unknown> | null;
  /**
   * The slide's layout IR, carried unvalidated so a native PPTX export
   * survives an edit. The server validates it (optionalLayoutIr) before use.
   */
  layoutIr?: unknown;
};

export type EditableDocumentSnapshot = {
  docId: string;
  fileName: string;
  fileType: string;
  jobId: string;
  pages: EditableDocumentPage[];
  /** Page count the document is expected to have (generation plan), 0 if unknown. */
  plannedPageCount: number;
  /** Every expected page is present with renderable HTML. */
  complete: boolean;
  /** Latest revision id already applied (from an edit patch), for lineage. */
  latestRevisionId: string;
};

export type DocumentEditTargetCandidate = {
  componentId: string;
  fileName: string;
  componentName?: string;
};

export type DocumentEditTargetResolution =
  | {
      status: "resolved";
      componentId: string;
      fileName: string;
      componentName?: string;
      reason: "explicit_component_id" | "exact_file_name" | "single_candidate";
    }
  | { status: "none" }
  | { status: "ambiguous"; reason: string; candidates: DocumentEditTargetCandidate[] };

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function str(value: unknown): string {
  return typeof value === "string" || typeof value === "number" ? String(value).trim() : "";
}

function parseMaybeJson(value: unknown): unknown {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed || !(trimmed.startsWith("{") || trimmed.startsWith("["))) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

function dedupeCandidates(candidates: DocumentEditTargetCandidate[]): DocumentEditTargetCandidate[] {
  const byId = new Map<string, DocumentEditTargetCandidate>();
  for (const candidate of candidates) {
    const componentId = str(candidate?.componentId);
    if (!componentId) continue;
    const existing = byId.get(componentId);
    byId.set(componentId, {
      componentId,
      fileName: existing?.fileName || str(candidate.fileName),
      componentName: existing?.componentName || str(candidate.componentName) || undefined,
    });
  }
  return Array.from(byId.values());
}

/**
 * Resolves which rendered document an edit targets:
 * explicit componentId → exact fileName(+extension) → the only candidate.
 * Never guesses between several visible documents.
 */
export function resolveDocumentEditTarget(params: {
  explicitComponentId?: string;
  fileName?: string;
  candidates: DocumentEditTargetCandidate[];
  /** The request carries its own document content (e.g. a stored file), so no rendered target is required. */
  hasStandaloneSource?: boolean;
}): DocumentEditTargetResolution {
  const candidates = dedupeCandidates(params.candidates || []);
  const explicitComponentId = str(params.explicitComponentId);
  const fileName = str(params.fileName);
  const exactMatches = fileName
    ? candidates.filter((candidate) => documentFileNamesMatch(candidate.fileName, fileName))
    : [];

  if (explicitComponentId) {
    const explicit = candidates.find((candidate) => candidate.componentId === explicitComponentId);
    const explicitConflicts =
      !!fileName &&
      !!explicit?.fileName &&
      !documentFileNamesMatch(explicit.fileName, fileName) &&
      exactMatches.some((candidate) => candidate.componentId !== explicitComponentId);
    if (explicitConflicts) {
      return {
        status: "ambiguous",
        reason: `Component ${explicitComponentId} shows "${explicit?.fileName}", but "${fileName}" is a different open document.`,
        candidates: [explicit as DocumentEditTargetCandidate, ...exactMatches],
      };
    }
    return {
      status: "resolved",
      componentId: explicitComponentId,
      fileName: explicit?.fileName || fileName,
      componentName: explicit?.componentName,
      reason: "explicit_component_id",
    };
  }

  if (exactMatches.length === 1) {
    const match = exactMatches[0];
    return {
      status: "resolved",
      componentId: match.componentId,
      fileName: match.fileName,
      componentName: match.componentName,
      reason: "exact_file_name",
    };
  }
  if (exactMatches.length > 1) {
    return {
      status: "ambiguous",
      reason: `${exactMatches.length} open documents are named "${fileName}".`,
      candidates: exactMatches,
    };
  }

  if (candidates.length === 1 && !params.hasStandaloneSource) {
    const only = candidates[0];
    // A named request that clearly points elsewhere must not silently land on
    // the only open document.
    if (fileName && only.fileName && !documentFileNamesMatch(only.fileName, fileName) && /\.[a-z0-9]{1,8}$/i.test(fileName)) {
      return { status: "none" };
    }
    return {
      status: "resolved",
      componentId: only.componentId,
      fileName: only.fileName || fileName,
      componentName: only.componentName,
      reason: "single_candidate",
    };
  }
  if (candidates.length > 1 && !params.hasStandaloneSource) {
    return {
      status: "ambiguous",
      reason: fileName
        ? `No open document is named "${fileName}", and ${candidates.length} documents are open.`
        : `${candidates.length} documents are open and the request does not say which one to edit.`,
      candidates,
    };
  }
  return { status: "none" };
}

type PageArraySource = { pages: unknown[]; identity: DocumentNodeIdentity };

type DocumentNodeIdentity = {
  docId: string;
  fileName: string;
  fileType: string;
  jobId: string;
  plannedPageCount: number;
};

function readIdentity(node: UnknownRecord, inherited: DocumentNodeIdentity): DocumentNodeIdentity {
  const metadata = isRecord(node.metadata) ? node.metadata : {};
  const documentContent = isRecord(node.documentContent) ? node.documentContent : {};
  const displayData = isRecord(node.displayData) ? node.displayData : {};
  const displayMetadata = isRecord(displayData.metadata) ? displayData.metadata : {};
  const displayContent = isRecord(displayData.documentContent) ? displayData.documentContent : {};
  const generationPlan = [
    node.generationPlan,
    documentContent.generationPlan,
    displayData.generationPlan,
    displayContent.generationPlan,
  ]
    .map(parseMaybeJson)
    .find(isRecord);
  const planPages = generationPlan && Array.isArray(generationPlan.pages) ? generationPlan.pages.length : 0;
  const first = (...values: unknown[]) => values.map(str).find(Boolean) || "";
  return {
    docId:
      first(
        node.docId,
        metadata.docId,
        documentContent.id,
        displayMetadata.docId,
        displayContent.id,
        node.documentId,
        node.id,
      ) || inherited.docId,
    fileName:
      first(
        node.fileName,
        node.filename,
        metadata.fileName,
        documentContent.fileName,
        displayMetadata.fileName,
        displayContent.fileName,
        node.title,
      ) || inherited.fileName,
    fileType: first(node.fileType, metadata.fileType, displayMetadata.fileType) || inherited.fileType,
    jobId:
      first(
        node.jobTask,
        node.jobId,
        metadata.jobTask,
        metadata.jobId,
        documentContent.jobTask,
        documentContent.jobId,
        displayData.jobTask,
        displayData.jobId,
        displayMetadata.jobTask,
        displayMetadata.jobId,
      ) || inherited.jobId,
    plannedPageCount: planPages || inherited.plannedPageCount,
  };
}

function pageArraysOf(node: UnknownRecord): unknown[][] {
  const documentContent = isRecord(node.documentContent) ? node.documentContent : {};
  const displayData = isRecord(node.displayData) ? node.displayData : {};
  const displayContent = isRecord(displayData.documentContent) ? displayData.documentContent : {};
  return [
    node.pages,
    node.htmlPages,
    documentContent.htmlPages,
    documentContent.pages,
    displayData.pages,
    displayContent.htmlPages,
    displayContent.pages,
  ].filter((value): value is unknown[] => Array.isArray(value));
}

export function normalizeEditablePages(rawPages: unknown[]): EditableDocumentPage[] {
  const byNumber = new Map<number, EditableDocumentPage>();
  rawPages.forEach((raw, index) => {
    if (!isRecord(raw)) return;
    const pageNumber = Number(raw.pageNumber) || Number(raw.page) || index + 1;
    const html = str(raw.html) || str(raw.content) || str(raw.text) || str(raw.body);
    const existing = byNumber.get(pageNumber);
    if (existing && existing.html) return;
    byNumber.set(pageNumber, {
      ...(str(raw.pageId) ? { pageId: str(raw.pageId) } : {}),
      pageNumber,
      title: str(raw.title) || `Page ${pageNumber}`,
      html: typeof raw.html === "string" && raw.html.trim()
        ? raw.html
        : typeof raw.content === "string" && raw.content.trim()
          ? raw.content
          : html,
      ...(isRecord(raw.qa) ? { qa: raw.qa } : {}),
      ...(isRecord(raw.layoutIr) ? { layoutIr: raw.layoutIr } : {}),
    });
  });
  return Array.from(byNumber.values()).sort((a, b) => a.pageNumber - b.pageNumber);
}

const EMPTY_IDENTITY: DocumentNodeIdentity = { docId: "", fileName: "", fileType: "", jobId: "", plannedPageCount: 0 };

/** The document node DocumentViewer renders: the first file of rawData, else the payload itself. */
function resolveDisplayedDocumentNode(root: unknown): UnknownRecord | null {
  let current = parseMaybeJson(root);
  for (let depth = 0; depth < 6; depth += 1) {
    if (Array.isArray(current)) {
      current = parseMaybeJson(current[0]);
      continue;
    }
    if (!isRecord(current)) return null;
    if (current.rawData !== undefined && current.rawData !== null && pageArraysOf(current).length === 0) {
      current = parseMaybeJson(current.rawData);
      continue;
    }
    return current;
  }
  return null;
}

function findPageArraySource(node: UnknownRecord): PageArraySource | null {
  const queue: Array<{ value: unknown; identity: DocumentNodeIdentity }> = [
    { value: node, identity: readIdentity(node, EMPTY_IDENTITY) },
  ];
  const seen = new Set<unknown>();
  while (queue.length > 0 && seen.size < 100) {
    const { value: rawValue, identity: inherited } = queue.shift() as { value: unknown; identity: DocumentNodeIdentity };
    const value = parseMaybeJson(rawValue);
    if (Array.isArray(value)) {
      if (value.length > 0) queue.push({ value: value[0], identity: inherited });
      continue;
    }
    if (!isRecord(value) || seen.has(value)) continue;
    seen.add(value);
    const identity = value === node ? inherited : readIdentity(value, inherited);
    const arrays = pageArraysOf(value);
    if (arrays.length > 0) {
      // Prefer the array that carries the most renderable HTML for this node.
      const best = arrays
        .map((pages) => ({ pages, htmlCount: normalizeEditablePages(pages).filter((page) => page.html).length }))
        .sort((a, b) => b.htmlCount - a.htmlCount)[0];
      return { pages: best.pages, identity };
    }
    for (const key of ["displayData", "documentContent", "rawData", "fetchedData", "data", "payload"]) {
      if (value[key] !== undefined && value[key] !== null) queue.push({ value: value[key], identity });
    }
  }
  return null;
}

type EditPatchLike = {
  docId?: unknown;
  fileName?: unknown;
  changedPages?: unknown;
  revision?: unknown;
  editedAt?: unknown;
};

function readPatch(raw: unknown): EditPatchLike | null {
  const parsed = parseMaybeJson(raw);
  return isRecord(parsed) && Array.isArray(parsed.changedPages) ? (parsed as EditPatchLike) : null;
}

/**
 * Overlays an edit patch's changed pages onto a complete page set. Unknown
 * page numbers are appended so a patch never loses content.
 */
export function overlayDocumentEditPatchPages(
  pages: EditableDocumentPage[],
  patch: unknown,
): EditableDocumentPage[] {
  const normalizedPatch = readPatch(patch);
  if (!normalizedPatch) return pages;
  const changes = normalizeEditablePages(normalizedPatch.changedPages as unknown[]).filter((page) => page.html);
  if (changes.length === 0) return pages;
  const byNumber = new Map(pages.map((page) => [page.pageNumber, page]));
  for (const change of changes) {
    const existing = byNumber.get(change.pageNumber);
    byNumber.set(change.pageNumber, {
      ...(existing || {}),
      ...change,
      pageId: existing?.pageId || change.pageId,
      title: change.title || existing?.title || `Page ${change.pageNumber}`,
      qa: change.qa ?? null,
    });
  }
  return Array.from(byNumber.values()).sort((a, b) => a.pageNumber - b.pageNumber);
}

function patchTimestamp(patch: EditPatchLike): number {
  const revision = isRecord(patch.revision) ? patch.revision : {};
  const parsed = Date.parse(str(patch.editedAt) || str(revision.createdAt));
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Overlays a component's edit patches (base, then latest) onto a page set,
 * skipping patches that are not newer than `newerThan` (ISO timestamp) — used
 * when the base pages already come from a durable revision.
 */
export function applyComponentEditPatches(
  pages: EditableDocumentPage[],
  props: unknown,
  options: { newerThan?: string } = {},
): EditableDocumentPage[] {
  const root = parseMaybeJson(props);
  if (!isRecord(root)) return pages;
  const threshold = options.newerThan ? Date.parse(options.newerThan) : Number.NaN;
  let result = pages;
  for (const raw of [root.documentEditBasePatch, root.documentEditPatch]) {
    const patch = readPatch(raw);
    if (!patch) continue;
    if (Number.isFinite(threshold) && patchTimestamp(patch) <= threshold) continue;
    result = overlayDocumentEditPatchPages(result, patch);
  }
  return result;
}

function patchDocId(patch: EditPatchLike): string {
  return str(patch.docId);
}

/**
 * Folds two edit patches of the same document into one cumulative patch:
 * every page either patch changed, the later patch winning per page. A patch
 * for a different document replaces the base instead of mixing documents.
 */
export function mergeDocumentEditPatches(base: unknown, latest: unknown): UnknownRecord | null {
  const later = readPatch(latest);
  const earlier = readPatch(base);
  if (!later) return earlier ? { ...(earlier as UnknownRecord) } : null;
  if (!earlier) return { ...(later as UnknownRecord) };
  const earlierDocId = patchDocId(earlier);
  const laterDocId = patchDocId(later);
  if (earlierDocId && laterDocId && earlierDocId !== laterDocId) return { ...(later as UnknownRecord) };
  const pagesByNumber = new Map<number, unknown>();
  for (const patch of [earlier, later]) {
    for (const page of patch.changedPages as unknown[]) {
      const pageNumber = isRecord(page) ? Number(page.pageNumber) : Number.NaN;
      if (Number.isFinite(pageNumber) && pageNumber > 0) pagesByNumber.set(pageNumber, page);
    }
  }
  return {
    ...(later as UnknownRecord),
    docId: laterDocId || earlierDocId || undefined,
    changedPages: Array.from(pagesByNumber.entries())
      .sort(([a], [b]) => a - b)
      .map(([, page]) => page),
  };
}

function patchIdentity(patch: unknown): string {
  const normalized = readPatch(patch);
  if (!normalized) return "";
  const record = normalized as UnknownRecord;
  return str(record.patchId) || patchRevisionId(normalized) || str(normalized.editedAt);
}

/**
 * Prepares an incoming `edit_component` prop update so a new document edit
 * does not erase earlier ones: the component's current latest patch is folded
 * into `documentEditBasePatch`, and the incoming patch becomes the latest
 * (the one the viewer's revert toggle undoes). Non-document updates and
 * re-deliveries of the same patch pass through unchanged.
 */
export function foldDocumentEditPatchProps<T>(existingProps: unknown, incomingProps: T): T {
  if (!isRecord(incomingProps) || !readPatch(incomingProps.documentEditPatch)) return incomingProps;
  const existing = parseMaybeJson(existingProps);
  if (!isRecord(existing)) return incomingProps;
  const previousLatest = readPatch(existing.documentEditPatch);
  if (!previousLatest) return incomingProps;
  const incomingIdentity = patchIdentity(incomingProps.documentEditPatch);
  if (incomingIdentity && incomingIdentity === patchIdentity(previousLatest)) return incomingProps;

  const incomingDocId = patchDocId(readPatch(incomingProps.documentEditPatch) as EditPatchLike);
  const previousDocId = patchDocId(previousLatest);
  const sameDocument = !incomingDocId || !previousDocId || incomingDocId === previousDocId;
  return {
    ...incomingProps,
    documentEditBasePatch: sameDocument
      ? mergeDocumentEditPatches(existing.documentEditBasePatch, previousLatest)
      : null,
  } as T;
}

/**
 * The client's current (folded) edit patches for one open document component.
 * Sent with an assistant request so an edit builds on what the user sees even
 * when the saved workspace still lags behind (e.g. an earlier edit whose
 * durable persist failed before the workspace save).
 */
export type DocumentEditOverlay = {
  componentId: string;
  docId: string;
  documentEditPatch: UnknownRecord | null;
  documentEditBasePatch: UnknownRecord | null;
};

export const MAX_DOCUMENT_EDIT_OVERLAYS = 4;
/** Total UTF-8 bytes of all overlays of one request. */
export const MAX_DOCUMENT_EDIT_OVERLAY_BYTES = 256 * 1024;
/**
 * Upper bound for a job-data write that carries overlays. Convex caps a
 * document at 1 MiB; the rest of the job row (status, metadata, results)
 * needs the remaining headroom.
 */
export const MAX_DOCUMENT_EDIT_OVERLAY_JOB_DATA_BYTES = 768 * 1024;

const utf8Encoder = new TextEncoder();

function jsonUtf8Bytes(value: unknown): number {
  return utf8Encoder.encode(JSON.stringify(value) ?? "").byteLength;
}

function readDocumentEditOverlay(componentId: string, props: unknown): DocumentEditOverlay | null {
  const root = parseMaybeJson(props);
  if (!componentId || !isRecord(root)) return null;
  const latest = readPatch(root.documentEditPatch);
  if (!latest) return null;
  const docId = patchDocId(latest);
  if (!docId) return null;
  const base = readPatch(root.documentEditBasePatch);
  return {
    componentId,
    docId,
    documentEditPatch: { ...(latest as UnknownRecord) },
    // A base patch for another (or an unknown) document is never mixed in.
    documentEditBasePatch: base && patchDocId(base) === docId ? { ...(base as UnknownRecord) } : null,
  };
}

function capDocumentEditOverlays(overlays: DocumentEditOverlay[]): DocumentEditOverlay[] {
  const kept: DocumentEditOverlay[] = [];
  let bytes = 0;
  for (const overlay of overlays) {
    if (kept.length >= MAX_DOCUMENT_EDIT_OVERLAYS) break;
    const size = jsonUtf8Bytes(overlay);
    if (bytes + size > MAX_DOCUMENT_EDIT_OVERLAY_BYTES) continue;
    bytes += size;
    kept.push(overlay);
  }
  return kept;
}

/** Client: collects the edit overlays of the open components (size-capped). */
export function buildDocumentEditOverlays(components: unknown): DocumentEditOverlay[] {
  if (!Array.isArray(components)) return [];
  const overlays: DocumentEditOverlay[] = [];
  for (const component of components) {
    if (!isRecord(component)) continue;
    const overlay = readDocumentEditOverlay(str(component.id), component.props);
    if (overlay) overlays.push(overlay);
  }
  return capDocumentEditOverlays(overlays);
}

/**
 * Server: re-validates untrusted request overlays with the same rules. The
 * size cap applies to the already-parsed request body; it bounds what is
 * forwarded and stored, not what the route parses.
 */
export function normalizeDocumentEditOverlays(value: unknown): DocumentEditOverlay[] {
  if (!Array.isArray(value)) return [];
  const overlays: DocumentEditOverlay[] = [];
  for (const candidate of value.slice(0, MAX_DOCUMENT_EDIT_OVERLAYS * 4)) {
    if (!isRecord(candidate)) continue;
    const overlay = readDocumentEditOverlay(str(candidate.componentId), candidate);
    if (overlay && overlay.docId === str(candidate.docId)) overlays.push(overlay);
  }
  return capDocumentEditOverlays(overlays);
}

/**
 * Adds overlays to a job-data object only while the whole write stays under
 * MAX_DOCUMENT_EDIT_OVERLAY_JOB_DATA_BYTES. Overlays are a best-effort hint:
 * ones that do not fit are dropped instead of failing the job insert.
 */
export function withDocumentEditOverlaysInJobData<T extends Record<string, unknown>>(
  data: T,
  overlays: DocumentEditOverlay[] | undefined,
): T & { documentEditOverlays?: DocumentEditOverlay[] } {
  if (!overlays?.length) return data;
  // `,"documentEditOverlays":[]` plus one comma per extra overlay.
  let bytes = jsonUtf8Bytes(data) + 26;
  const kept: DocumentEditOverlay[] = [];
  for (const overlay of overlays) {
    const size = jsonUtf8Bytes(overlay) + (kept.length > 0 ? 1 : 0);
    if (bytes + size > MAX_DOCUMENT_EDIT_OVERLAY_JOB_DATA_BYTES) continue;
    bytes += size;
    kept.push(overlay);
  }
  return kept.length ? { ...data, documentEditOverlays: kept } : data;
}

/**
 * Makes the client's overlay the edit patches of the target component
 * payload. Applies only to the same component and document id, and only when
 * the overlay's latest patch is the saved one or newer than it: a stale tab
 * must not roll back a newer saved edit. Returns null (payload unchanged)
 * otherwise.
 */
export function applyDocumentEditOverlay<T>(
  payload: T,
  params: { overlays: unknown; componentId: string; docId: string },
): T | null {
  const componentId = str(params.componentId);
  const docId = str(params.docId);
  const root = parseMaybeJson(payload);
  if (!componentId || !docId || !isRecord(root)) return null;
  const overlay = normalizeDocumentEditOverlays(params.overlays).find(
    (candidate) => candidate.componentId === componentId,
  );
  if (!overlay || overlay.docId !== docId) return null;
  const saved = readPatch(root.documentEditPatch);
  const incoming = readPatch(overlay.documentEditPatch);
  if (saved && incoming && patchIdentity(saved) !== patchIdentity(incoming)) {
    const savedAt = patchTimestamp(saved);
    const incomingAt = patchTimestamp(incoming);
    // Unknown or equal timestamps keep the saved patch.
    if (!(incomingAt > savedAt)) return null;
  }
  return {
    ...root,
    documentEditPatch: overlay.documentEditPatch,
    documentEditBasePatch: overlay.documentEditBasePatch,
  } as T;
}

/** document_operations wraps rendered context as `{prev_document_context}`; that is not a document source. */
export function isPrevDocumentContextWrapper(chainValue: unknown): boolean {
  const parsed = parseMaybeJson(chainValue);
  return isRecord(parsed) && Object.keys(parsed).length === 1 && "prev_document_context" in parsed;
}

function patchRevisionId(patch: unknown): string {
  const normalized = readPatch(patch);
  if (!normalized) return "";
  const revision = isRecord(normalized.revision) ? normalized.revision : {};
  return str(revision.revisionId);
}

/**
 * Extracts the complete, untruncated page set of the document a rendered
 * component shows (the first document in its rawData, as DocumentViewer
 * renders it), with any AI edit patches folded on top.
 */
export function extractEditableDocumentFromComponentProps(props: unknown): EditableDocumentSnapshot | null {
  const root = parseMaybeJson(props);
  const node = resolveDisplayedDocumentNode(root);
  if (!node) return null;
  const source = findPageArraySource(node);
  const identity = source?.identity || readIdentity(node, EMPTY_IDENTITY);
  const basePatch = isRecord(root) ? root.documentEditBasePatch : undefined;
  const latestPatch = isRecord(root) ? root.documentEditPatch : undefined;
  const pages = applyComponentEditPatches(source ? normalizeEditablePages(source.pages) : [], root);
  const htmlPages = pages.filter((page) => page.html.trim());
  const plannedPageCount = identity.plannedPageCount;
  const complete =
    htmlPages.length > 0 &&
    htmlPages.length === pages.length &&
    (plannedPageCount === 0 || htmlPages.length >= plannedPageCount);

  if (!identity.docId && !identity.fileName && htmlPages.length === 0) return null;
  return {
    docId: identity.docId,
    fileName: identity.fileName,
    fileType: identity.fileType,
    jobId: identity.jobId,
    pages: htmlPages,
    plannedPageCount,
    complete,
    latestRevisionId: patchRevisionId(latestPatch) || patchRevisionId(basePatch),
  };
}

/** Epoch ms from a number or a date string; date strings (e.g. "2026") parse first, epoch-ms strings fall back. */
function toEpochMs(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const trimmed = value.trim();
    const parsed = Date.parse(trimmed);
    if (Number.isFinite(parsed)) return parsed;
    const numeric = Number(trimmed);
    if (Number.isFinite(numeric)) return numeric;
  }
  return 0;
}

/**
 * Chooses between the pages embedded in the displayed data and the pages of
 * the document's latest durable (job-bound) revision.
 *
 * - A deliberately reverted edit always keeps the embedded copy.
 * - The durable revision wins when the embedded copy is a partial subset.
 * - It wins over the original generation (no displayed revision) once an
 *   AI-edit revision exists, e.g. after a reload that lost the edit patch.
 * - It wins over a different, older displayed revision: an edit persisted as
 *   R2 whose patch never reached the saved workspace must not lose to a
 *   reloaded R1. A newer locally applied edit keeps the embedded copy.
 */
export function selectDisplayedDocumentPages<T>(params: {
  staticPages: T[];
  durablePages: T[];
  displayedRevisionId?: unknown;
  durableRevisionId?: unknown;
  /** When the displayed revision was applied (ISO string or epoch ms). */
  displayedAt?: unknown;
  /** When the durable revision was persisted (ISO string or epoch ms). */
  durablePersistedAt?: unknown;
  editReverted?: boolean;
}): T[] {
  const { staticPages, durablePages } = params;
  if (staticPages.length === 0) return durablePages;
  if (durablePages.length === 0) return staticPages;
  if (params.editReverted) return staticPages;
  if (durablePages.length > staticPages.length) return durablePages;
  const displayedRevisionId = typeof params.displayedRevisionId === "string" ? params.displayedRevisionId.trim() : "";
  const durableRevisionId = typeof params.durableRevisionId === "string" ? params.durableRevisionId.trim() : "";
  if (!durableRevisionId || durableRevisionId === displayedRevisionId) return staticPages;
  if (!displayedRevisionId) return durablePages;
  const durablePersistedAt = toEpochMs(params.durablePersistedAt);
  const displayedAt = toEpochMs(params.displayedAt);
  if (durablePersistedAt > 0 && displayedAt > 0 && durablePersistedAt > displayedAt) return durablePages;
  return staticPages;
}

const CROSS_PAGE_CUE_PATTERN =
  /\b(previous|next|other|another|earlier|later|above|below|preceding|following|consistent|consistency|match(?:es|ing)?|same as|like the|rest of|elsewhere|throughout|across|every (?:page|slide|section)|all (?:the )?(?:pages|slides|sections)|each (?:page|slide|section)|(?:first|last|final|opening|closing|title|cover) (?:page|slide|section)|from (?:the )?(?:page|slide|section)|(?:pages|slides|sections))\b/i;
const PAGE_NUMBER_REFERENCE_PATTERN = /\b(?:page|slide|section|p\.)\s*#?\s*(\d{1,4})\b/gi;

/**
 * True when an edit request refers to content on pages other than the ones
 * being edited, so a cross-page context plan is worth an LLM call. A plain
 * "rewrite page 2" is a self reference and needs no planner.
 */
export function editPromptReferencesOtherPages(prompt: string, targetPageNumbers: number[]): boolean {
  const text = String(prompt || "");
  const targets = new Set(targetPageNumbers.filter((n) => Number.isFinite(n) && n > 0));
  const pageReference = new RegExp(PAGE_NUMBER_REFERENCE_PATTERN.source, "gi");
  let match: RegExpExecArray | null;
  while ((match = pageReference.exec(text)) !== null) {
    const referenced = Number(match[1]);
    if (referenced > 0 && !targets.has(referenced)) return true;
  }
  return CROSS_PAGE_CUE_PATTERN.test(text.replace(PAGE_NUMBER_REFERENCE_PATTERN, " "));
}
